import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Check, X as XIcon, Lock } from 'lucide-react';
import i18n from '../i18n';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import api from '../api/client';
import { normalizeUnit, kmToUnit, unitToKm, formatDistanceKm, distanceUnitLabel } from '../utils/units';
import './Account.css';

interface Subscription {
  id: string;
  device_id: string;
  status: string;
  package_type: string;
  started_at: string;
  expires_at: string | null;
  activated_at: string | null;
}

interface AlertConfig {
  id: string;
  device_id: string;
  alert_type: string;
  enabled: boolean;
  threshold: number | null;
  notify_push: boolean;
  notify_sms: boolean;
}

interface Device { id: string; name: string; plate_number?: string | null; preferred_unit?: string; }

interface Odometer { total_distance_km: number | null; calibrated_at: string | null; }

const ALERT_ORDER = ['towing', 'geofence_exit', 'voltage_low', 'voltage_critical', 'voltage_charging'];
const ALERT_EMOJI: Record<string, string> = {
  towing: '🔴', geofence_exit: '🟠', voltage_low: '🟡',
  voltage_critical: '🔴', voltage_charging: '🟢',
};

const LANGUAGES = [
  { code: 'pl', flag: '🇵🇱', label: 'Polski' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch', soon: true },
  { code: 'fr', flag: '🇫🇷', label: 'Français', soon: true },
];

export default function Account() {
  const { t } = useTranslation('alerts');
  const { t: tc } = useTranslation('common');
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const { status: pushStatus, subscribe, unsubscribe, testPush, isSubscribed, isSupported } = usePushNotifications();

  const [configs, setConfigs] = useState<AlertConfig[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [testSent, setTestSent] = useState(false);
  const [langSaving, setLangSaving] = useState(false);
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPlate, setEditPlate] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [odometers, setOdometers] = useState<Record<string, Odometer>>({});
  const [editingOdometerId, setEditingOdometerId] = useState<string | null>(null);
  const [editKm, setEditKm] = useState('');
  const [savingOdometer, setSavingOdometer] = useState(false);
  const [unitSaving, setUnitSaving] = useState<string | null>(null);
  const [immobilized, setImmobilized] = useState<Record<string, boolean | null>>({});
  const [doutSending, setDoutSending] = useState<string | null>(null);
  const [doutMessage, setDoutMessage] = useState<Record<string, string>>({});

  useEffect(() => {
    api.get('/devices/').then((r) => {
      setDevices(r.data);
      Promise.all(
        (r.data as Device[]).map((d: Device) =>
          api.get(`/devices/${d.id}/odometer`).then((res) => [d.id, res.data] as const).catch(() => null)
        )
      ).then((results) => {
        const map: Record<string, Odometer> = {};
        results.forEach((r2) => { if (r2) map[r2[0]] = r2[1]; });
        setOdometers(map);
      });
      Promise.all(
        (r.data as Device[]).map((d: Device) =>
          api.get(`/devices/${d.id}/last-position`).then((res) => [d.id, res.data] as const).catch(() => null)
        )
      ).then((results) => {
        const map: Record<string, boolean | null> = {};
        results.forEach((r2) => { if (r2) map[r2[0]] = r2[1]?.attributes?.out1 ?? null; });
        setImmobilized(map);
      });
    }).catch(() => {});
    api.get('/alerts/config').then((r) => setConfigs(r.data)).catch(() => {});
    api.get('/subscriptions/').then((r) => setSubscriptions(r.data)).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const changeLanguage = async (lang: string) => {
    if (lang === user?.preferred_language) return;
    setLangSaving(true);
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem('i18n_lang', lang);
      await api.patch('/auth/me', { preferred_language: lang });
      updateUser({ preferred_language: lang });
    } catch { /* ignore */ }
    setLangSaving(false);
  };

  const changeDeviceUnit = async (deviceId: string, newUnit: 'km' | 'mi') => {
    setUnitSaving(deviceId);
    try {
      const res = await api.patch(`/devices/${deviceId}/unit`, { preferred_unit: newUnit });
      setDevices((prev) => prev.map((d) => (d.id === deviceId ? { ...d, preferred_unit: res.data.preferred_unit } : d)));
    } catch { /* ignore */ }
    setUnitSaving(null);
  };

  const toggleConfig = async (cfg: AlertConfig) => {
    setSaving(cfg.id);
    try {
      const res = await api.post('/alerts/config', { ...cfg, enabled: !cfg.enabled });
      setConfigs((prev) => prev.map((c) => (c.id === cfg.id ? res.data : c)));
    } catch { /* ignore */ }
    setSaving(null);
  };

  const startRename = (device: Device) => {
    setEditingDeviceId(device.id);
    setEditName(device.name);
    setEditPlate(device.plate_number || '');
  };

  const cancelRename = () => {
    setEditingDeviceId(null);
    setEditName('');
    setEditPlate('');
  };

  const saveRename = async (deviceId: string) => {
    const trimmed = editName.trim();
    if (!trimmed) { cancelRename(); return; }
    setRenaming(true);
    try {
      const res = await api.patch(`/devices/${deviceId}`, { name: trimmed, plate_number: editPlate.trim() || null });
      setDevices((prev) => prev.map((d) => (
        d.id === deviceId ? { ...d, name: res.data.name, plate_number: res.data.plate_number } : d
      )));
    } catch { /* ignore */ }
    setRenaming(false);
    cancelRename();
  };

  const startOdometerEdit = (deviceId: string) => {
    setEditingOdometerId(deviceId);
    const km = odometers[deviceId]?.total_distance_km;
    const unit = normalizeUnit(devices.find((d) => d.id === deviceId)?.preferred_unit);
    setEditKm(km != null ? kmToUnit(km, unit).toFixed(0) : '');
  };

  const cancelOdometerEdit = () => {
    setEditingOdometerId(null);
    setEditKm('');
  };

  const saveOdometer = async (deviceId: string) => {
    const enteredValue = parseFloat(editKm);
    if (Number.isNaN(enteredValue)) { cancelOdometerEdit(); return; }
    const unit = normalizeUnit(devices.find((d) => d.id === deviceId)?.preferred_unit);
    const km = unitToKm(enteredValue, unit);
    setSavingOdometer(true);
    try {
      const res = await api.patch(`/devices/${deviceId}/odometer`, { current_km: km });
      setOdometers((prev) => ({ ...prev, [deviceId]: res.data }));
    } catch { /* ignore */ }
    setSavingOdometer(false);
    cancelOdometerEdit();
  };

  const toggleImmobilizer = async (deviceId: string, deviceName: string) => {
    const current = immobilized[deviceId];
    const nextState = !current;
    const question = nextState
      ? `Unieruchomić silnik "${deviceName}"? Rozrusznik/zapłon zostaną zablokowane do czasu odblokowania.`
      : `Odblokować silnik "${deviceName}"?`;
    if (!window.confirm(question)) return;
    setDoutSending(deviceId);
    setDoutMessage((prev) => ({ ...prev, [deviceId]: '' }));
    try {
      const res = await api.post(`/devices/${deviceId}/dout1`, { state: nextState, confirm: true });
      setDoutMessage((prev) => ({ ...prev, [deviceId]: res.data.message || '' }));
      if (res.data.status === 'sent') {
        setImmobilized((prev) => ({ ...prev, [deviceId]: nextState }));
      }
    } catch (err: any) {
      setDoutMessage((prev) => ({ ...prev, [deviceId]: err?.response?.data?.detail || 'Błąd wysyłania komendy' }));
    }
    setDoutSending(null);
  };

  const handleTest = async () => {
    const ok = await testPush();
    if (ok) { setTestSent(true); setTimeout(() => setTestSent(false), 3000); }
  };

  const groupedByDevice = devices.map((d) => ({
    device: d,
    configs: ALERT_ORDER
      .map((type) => configs.find((c) => c.device_id === d.id && c.alert_type === type))
      .filter(Boolean) as AlertConfig[],
  }));

  const currentLang = user?.preferred_language || 'pl';

  return (
    <div className="account-page">
      <div className="account-header">
        <div className="account-avatar">
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </div>
        <div>
          <p className="account-name">{user?.first_name} {user?.last_name}</p>
          <p className="account-email">{user?.email}</p>
        </div>
      </div>

      {/* Language selector */}
      <div className="account-section">
        <h3>{tc('language.select')}</h3>
        <div className="lang-grid">
          {LANGUAGES.map(({ code, flag, label, soon }) => (
            <button
              key={code}
              className={`lang-btn ${currentLang === code ? 'active' : ''} ${soon ? 'soon' : ''}`}
              onClick={() => !soon && changeLanguage(code)}
              disabled={soon || langSaving}
              title={soon ? 'Wkrótce dostępne' : label}
            >
              <span className="lang-flag">{flag}</span>
              <span className="lang-label">{label}</span>
              {soon && <span className="lang-soon">soon</span>}
            </button>
          ))}
        </div>
      </div>

      {subscriptions.map((sub) => {
        const device = devices.find((d) => d.id === sub.device_id);
        const expiresAt = sub.expires_at ? new Date(sub.expires_at) : null;
        const now = new Date();
        const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / 86400000)) : null;
        const totalDays = 365;
        const startedAt = sub.started_at ? new Date(sub.started_at) : null;
        const elapsed = startedAt && expiresAt ? expiresAt.getTime() - startedAt.getTime() : totalDays * 86400000;
        const progressPct = daysLeft !== null ? Math.max(0, Math.min(100, (daysLeft / (elapsed / 86400000)) * 100)) : 100;
        const isExpired = sub.status !== 'active' || (expiresAt !== null && expiresAt < now);

        return (
          <div className="account-section" key={sub.id}>
            {isExpired && (
              <div className="sub-expired-banner">Subskrypcja wygasła — odnów, aby kontynuować</div>
            )}
            <h3>Subskrypcja{device ? ` — ${device.name}` : ''}</h3>
            <div className="account-row">
              <span>Status</span>
              <span className={isExpired ? 'tag-expired' : 'tag-active'}>
                {isExpired ? 'Wygasła' : 'Aktywna'}
              </span>
            </div>
            {sub.package_type && (
              <div className="account-row">
                <span>Pakiet</span>
                <span>{sub.package_type === 'premium' ? 'Premium' : 'Standard'}</span>
              </div>
            )}
            {expiresAt && (
              <div className="account-row">
                <span>Wygasa</span>
                <span style={{ color: daysLeft !== null && daysLeft <= 30 ? '#ffc107' : '#ccc' }}>
                  {expiresAt.toLocaleDateString('pl-PL')}
                  {daysLeft !== null && ` (${daysLeft} dni)`}
                </span>
              </div>
            )}
            {daysLeft !== null && !isExpired && (
              <div className="sub-progress-wrap">
                <div className="sub-progress-bar">
                  <div className="sub-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
                <span className="sub-progress-label">{daysLeft} / {Math.round(elapsed / 86400000)} dni</span>
              </div>
            )}
            <button className="account-btn-outline" disabled>
              Odnów subskrypcję (wkrótce)
            </button>
          </div>
        );
      })}

      {/* Push notifications */}
      <div className="account-section">
        <h3>{t('settings.push_notifications')}</h3>
        {!isSupported ? (
          <p className="account-note">{tc('errors.no_connection')}</p>
        ) : (
          <>
            <div className="account-row">
              <div>
                <span>{t('settings.push_notifications')}</span>
                <p className="account-note">{t('settings.push_description')}</p>
              </div>
              <button
                className={`toggle-btn ${isSubscribed ? 'on' : 'off'}`}
                onClick={isSubscribed ? unsubscribe : subscribe}
                disabled={pushStatus === 'loading'}
              >
                {pushStatus === 'loading' ? '...' : isSubscribed ? 'Wł.' : 'Wył.'}
              </button>
            </div>
            {pushStatus === 'denied' && (
              <p className="account-note error">Zablokowane w przeglądarce.</p>
            )}
            {isSubscribed && (
              <div className="account-row">
                <span>{t('settings.test_push')}</span>
                <button className="btn-test" onClick={handleTest} disabled={testSent}>
                  {testSent ? 'Wysłano!' : t('settings.test_push')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Alert configs per device */}
      {groupedByDevice.map(({ device, configs: dcfgs }) => {
        const unit = normalizeUnit(device.preferred_unit);
        return (
        <div className="account-section" key={device.id}>
          {editingDeviceId === device.id ? (
            <div className="device-rename-form">
              <div className="device-rename-row">
                <input
                  className="device-rename-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nazwa"
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveRename(device.id);
                    if (e.key === 'Escape') cancelRename();
                  }}
                />
              </div>
              <div className="device-rename-row">
                <input
                  className="device-rename-input"
                  value={editPlate}
                  onChange={(e) => setEditPlate(e.target.value)}
                  placeholder="Nr rejestracyjny (opcjonalnie)"
                  maxLength={20}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveRename(device.id);
                    if (e.key === 'Escape') cancelRename();
                  }}
                />
                <button className="device-rename-btn" onClick={() => saveRename(device.id)} disabled={renaming} aria-label="Zapisz">
                  <Check size={16} />
                </button>
                <button className="device-rename-btn" onClick={cancelRename} disabled={renaming} aria-label="Anuluj">
                  <XIcon size={16} />
                </button>
              </div>
            </div>
          ) : (
            <h3 className="device-name-header">
              {t('settings.title')} — {device.name}
              {device.plate_number && <span className="device-plate-tag">{device.plate_number}</span>}
              <button className="device-rename-btn" onClick={() => startRename(device)} aria-label="Zmień nazwę i numer rejestracyjny">
                <Pencil size={14} />
              </button>
            </h3>
          )}
          <div className="account-row">
            <span>Jednostki</span>
            <div className="lang-grid lang-grid--compact">
              <button
                className={`lang-btn ${unit === 'km' ? 'active' : ''}`}
                onClick={() => changeDeviceUnit(device.id, 'km')}
                disabled={unitSaving === device.id}
              >
                <span className="lang-label">km</span>
              </button>
              <button
                className={`lang-btn ${unit === 'mi' ? 'active' : ''}`}
                onClick={() => changeDeviceUnit(device.id, 'mi')}
                disabled={unitSaving === device.id}
              >
                <span className="lang-label">mi</span>
              </button>
            </div>
          </div>
          {editingOdometerId === device.id ? (
            <div className="device-rename-row">
              <input
                className="device-rename-input"
                type="number"
                min={0}
                step={1}
                value={editKm}
                onChange={(e) => setEditKm(e.target.value)}
                placeholder={`Przebieg (${distanceUnitLabel(unit)})`}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveOdometer(device.id);
                  if (e.key === 'Escape') cancelOdometerEdit();
                }}
              />
              <button className="device-rename-btn" onClick={() => saveOdometer(device.id)} disabled={savingOdometer} aria-label="Zapisz">
                <Check size={16} />
              </button>
              <button className="device-rename-btn" onClick={cancelOdometerEdit} disabled={savingOdometer} aria-label="Anuluj">
                <XIcon size={16} />
              </button>
            </div>
          ) : (
            <div className="account-row">
              <div>
                <span>
                  Przebieg: {odometers[device.id]?.total_distance_km != null
                    ? formatDistanceKm(odometers[device.id].total_distance_km!, unit)
                    : 'nie ustawiono'}
                </span>
                {odometers[device.id]?.calibrated_at && (
                  <p className="account-note" style={{ margin: 0 }}>
                    ostatnio ustawiono: {new Date(odometers[device.id].calibrated_at!).toLocaleDateString('pl-PL')}
                  </p>
                )}
              </div>
              <button className="device-rename-btn" onClick={() => startOdometerEdit(device.id)} aria-label="Ustaw przebieg">
                <Pencil size={14} />
              </button>
            </div>
          )}
          <div className="account-row">
            <div>
              <span>Immobilizer (DOUT1)</span>
              <p className="account-note" style={{ margin: 0 }}>
                {immobilized[device.id] == null
                  ? 'Stan nieznany — brak danych z urządzenia'
                  : immobilized[device.id]
                    ? 'Silnik zablokowany'
                    : 'Silnik odblokowany'}
              </p>
              {doutMessage[device.id] && (
                <p className="account-note" style={{ margin: 0 }}>{doutMessage[device.id]}</p>
              )}
            </div>
            <button
              className="dout-btn"
              onClick={() => toggleImmobilizer(device.id, device.name)}
              disabled={doutSending === device.id}
              aria-label="Przełącz immobilizer (DOUT1)"
            >
              <Lock size={14} /> {immobilized[device.id] ? 'Odblokuj' : 'Zablokuj'}
            </button>
          </div>
          {dcfgs.length === 0 ? (
            <p className="account-note">{tc('errors.loading')}</p>
          ) : (
            dcfgs.map((cfg) => (
              <React.Fragment key={cfg.id}>
                <div className="account-row">
                  <span>{ALERT_EMOJI[cfg.alert_type]} {t(`types.${cfg.alert_type}`)}</span>
                  <button
                    className={`toggle-btn ${cfg.enabled ? 'on' : 'off'}`}
                    onClick={() => toggleConfig(cfg)}
                    disabled={saving === cfg.id}
                  >
                    {saving === cfg.id ? '...' : cfg.enabled ? 'Wł.' : 'Wył.'}
                  </button>
                </div>
                {cfg.alert_type === 'geofence_exit' && (
                  <div className="account-row geofence-setup-row">
                    <span className="account-note" style={{ margin: 0 }}>Strefa bezpieczna (centrum + promień)</span>
                    <button
                      className="btn-geofence-setup"
                      onClick={() => navigate(`/geofence/${device.id}`)}
                    >
                      Ustaw strefę →
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </div>
        );
      })}

      <div className="account-section">
        <h3>Konto</h3>
        {user?.account_type === 'admin' && (
          <button className="account-btn-admin" onClick={() => navigate('/admin')}>
            Panel Admina
          </button>
        )}
        <button className="account-logout" onClick={handleLogout}>
          Wyloguj się
        </button>
      </div>
    </div>
  );
}
